/**
 * Shared TTI Helpers
 *
 * Common utilities used by navigationTTI.ts:
 * - Constants
 * - Helper functions
 * - Observer factories
 * - Reporting function
 */

import PerformanceMonitoringService from "@/services/PerformanceMonitoringService";
import store from "@/store";

// ============================================================================
// Types
// ============================================================================

export interface NetworkRequestInfo {
  url: string;
  startTime: number; // ms since navigation start
  duration: number; // in ms
  bytesTransferred?: number; // bytes transferred (if available)
  initiatorType?: string; // resource type: xhr, fetch, script, css, img, font, link, etc.
}

export interface TTIReportData {
  type: "TTI" | "softnavigation";
  navigationId: string; // timestamp_sessionId for unique identification
  duration: number;
  rating: "good" | "needs-improvement" | "poor";
  fromRoute: string;
  toRoute: string;
  longTaskCount: number;
  totalBlockingTime: number;
  networkRequestCount: number;
  networkRequests: NetworkRequestInfo[];
  effectiveNetworkType: string | undefined; // 4g, 3g, 2g, slow-2g
  method: "navigation";
}

// ============================================================================
// Constants (based on Google's TTI polyfill recommendations)
// ============================================================================

export const NETWORK_QUIET_MS = 2000; // Time with no network activity to consider "network idle"
export const QUIET_WINDOW_MS = 5000; // TTI quiet window - 5 seconds of no activity to confirm stability
export const MAX_TTI_WAIT_MS = 30000; // Max time to wait for TTI (30 seconds)
export const MIN_VALID_TTI_MS = 10; // Minimum valid TTI to report
export const LONG_TASK_THRESHOLD_MS = 50; // Long task threshold

// User interaction tracking - only track TTI for user-initiated navigations
export const USER_INTERACTION_WINDOW_MS = 1000; // Time window to consider navigation as user-initiated

// ============================================================================
// Helper Functions
// ============================================================================

export const getSessionId = (): string | undefined => {
  try {
    return sessionStorage.getItem("sessionId") || undefined;
  } catch {
    return undefined;
  }
};

export const getUserId = (): string | undefined => {
  try {
    return store.state.user?.user?.id;
  } catch {
    return undefined;
  }
};

export const getLocationId = (): string | null => {
  try {
    const match = window.location.pathname.match(/\/location\/([^/]+)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
};

/**
 * Get effective network type (4g, 3g, 2g, slow-2g)
 */
export const getEffectiveNetworkType = (): string | undefined => {
  try {
    const nav = navigator as any;
    return nav.connection?.effectiveType;
  } catch {
    return undefined;
  }
};

/**
 * Get TTI rating based on duration
 */
export const getTTIRating = (
  tti: number
): "good" | "needs-improvement" | "poor" => {
  if (tti <= 1500) return "good";
  if (tti <= 3000) return "needs-improvement";
  return "poor";
};

/**
 * Calculate Total Blocking Time from long tasks
 */
export const calculateTBT = (
  longTasks: PerformanceEntry[],
  startTime: number
): number => {
  return longTasks
    .filter((task) => task.startTime >= startTime)
    .reduce((sum, task) => {
      const blocking = task.duration - LONG_TASK_THRESHOLD_MS;
      return sum + (blocking > 0 ? blocking : 0);
    }, 0);
};

// ============================================================================
// Reporting
// ============================================================================

/**
 * Report TTI to backend via PerformanceMonitoringService
 */
export const reportTTI = (data: TTIReportData): void => {
  if (data.duration < MIN_VALID_TTI_MS) {
    console.log(
      `[TTI] Skipping ${data.type}: ${data.duration}ms below threshold (${MIN_VALID_TTI_MS}ms)`
    );
    return;
  }

  const emoji =
    data.rating === "good"
      ? "\u{1F7E2}"
      : data.rating === "needs-improvement"
      ? "\u{1F7E1}"
      : "\u{1F534}";

  console.log(
    `[TTI] ${emoji} ${data.type}: ${data.fromRoute} -> ${data.toRoute}: ${data.duration}ms (${data.rating})`
  );

  PerformanceMonitoringService.logPageLoadPerformance({
    type: data.type,
    navigationId: data.navigationId,
    duration: data.duration,
    rating: data.rating,
    fromRoute: data.fromRoute,
    toRoute: data.toRoute,
    longTaskCount: data.longTaskCount,
    totalBlockingTime: data.totalBlockingTime,
    networkRequestCount: data.networkRequestCount,
    networkRequests: data.networkRequests,
    effectiveNetworkType: data.effectiveNetworkType,
    method: data.method,
    sessionId: getSessionId(),
    userId: getUserId(),
    locationId: getLocationId(),
    url: window.location.href,
    timestamp: new Date().toISOString(),
  });
};

// ============================================================================
// Observer Factories
// ============================================================================

/**
 * Create a Long Task observer
 */
export const createLongTaskObserver = (
  onLongTask: (entries: PerformanceEntry[]) => void
): PerformanceObserver | null => {
  if (typeof window === "undefined" || !("PerformanceObserver" in window)) {
    return null;
  }

  try {
    // Check if longtask is supported
    if (!PerformanceObserver.supportedEntryTypes?.includes("longtask")) {
      return null;
    }

    const observer = new PerformanceObserver((list) => {
      onLongTask(list.getEntries());
    });
    observer.observe({ type: "longtask", buffered: false });
    return observer;
  } catch {
    return null;
  }
};

/**
 * Create a MutationObserver for DOM changes
 */
export const createMutationObserver = (
  onMutation: () => void
): MutationObserver | null => {
  if (typeof MutationObserver === "undefined" || !document.body) {
    return null;
  }

  try {
    const observer = new MutationObserver(() => {
      onMutation();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false, // Skip attribute changes to reduce noise
    });

    return observer;
  } catch {
    return null;
  }
};

/**
 * Resource timing entry with typed properties
 */
export interface ResourceTimingEntry extends PerformanceEntry {
  initiatorType: string;
  transferSize: number;
  encodedBodySize: number;
  decodedBodySize: number;
  responseEnd: number;
  startTime: number;
  requestStart: number;
  responseStart: number;
}

/**
 * Create a PerformanceObserver for resource timing entries
 * Captures all network requests: xhr, fetch, script, css, img, font, link, etc.
 *
 * Note: CORS preflight (OPTIONS) requests are NOT exposed by the Resource Timing API
 * for security reasons - this is a browser limitation, not a bug.
 */
export const createResourceObserver = (
  onResource: (entries: ResourceTimingEntry[]) => void
): PerformanceObserver | null => {
  if (typeof window === "undefined" || !("PerformanceObserver" in window)) {
    return null;
  }

  try {
    // Check if resource is supported
    if (!PerformanceObserver.supportedEntryTypes?.includes("resource")) {
      return null;
    }

    const observer = new PerformanceObserver((list) => {
      onResource(list.getEntries() as ResourceTimingEntry[]);
    });
    // buffered: true captures resources that loaded just before observer started
    observer.observe({ type: "resource", buffered: true });
    return observer;
  } catch {
    return null;
  }
};
